export const SITE_TERMS_MD = `**Effective date: September 13, 2026**
**Last updated: September 13, 2026**

These Terms of Use (“Terms”) govern your access to and use of **buildmakewin.com** and its content (collectively, the “Site”).

The Site is operated by Build Make Win Ventures LLC (“Build Make Win,” “we,” “us,” or “our”), an Ohio limited liability company.

By accessing or using the Site, you agree to these Terms. If you do not agree, do not use the Site.

## 1. The Site

Build.Make.Win is a product company that develops and owns products, including CoachER. The Site provides general information about Build Make Win, its mission, and its products.

The Site does not currently provide user accounts, paid services, subscriptions, or payment processing.

Nothing on the Site is an offer to sell securities, an investment solicitation, a guarantee of product availability, or a promise of future performance.

## 2. Separate Product Terms

These Terms apply only to the Build.Make.Win corporate Site. Products owned or operated by Build Make Win may have separate terms, privacy policies, subscription terms, or end-user license terms.

Your use of CoachER is governed by the CoachER Terms of Use available at **buildmakewin.com/coacher/terms** and the CoachER Privacy Policy available at **buildmakewin.com/coacher/privacy**.

If these Terms conflict with product-specific terms regarding that product, the product-specific terms control.

## 3. Eligibility

You may use the Site only if you can legally enter into a binding agreement.

If you use the Site on behalf of a company, team, organization, or other entity, you represent that you have authority to bind that entity to these Terms.

The Site is not directed to children under 13. Children under 13 should not submit personal information through the Site.

## 4. Acceptable Use

You agree not to:

- Use the Site for unlawful, fraudulent, harmful, or abusive purposes;
- Attempt to gain unauthorized access to the Site, its systems, or connected networks;
- Interfere with the Site’s operation, security, or availability;
- Introduce malware, harmful code, automated attacks, or excessive traffic;
- Scrape, harvest, or use automated means to extract Site content or personal information, except as permitted by law or with our written permission;
- Impersonate another person or misrepresent your affiliation;
- Remove or alter copyright, trademark, or other proprietary notices; or
- Use the Site or its content to infringe or violate another person’s rights.

We may restrict or block access to protect the Site, our rights, or other users.

## 5. Intellectual Property

The Site and its contents—including text, graphics, logos, icons, brand elements, page designs, photographs, software, and compilations—are owned by Build Make Win or its licensors and are protected by intellectual-property and other laws.

Subject to these Terms, Build Make Win grants you a limited, revocable, non-exclusive, non-transferable license to access and use the Site for your personal or internal business evaluation.

This license does not permit you to copy, modify, distribute, sell, license, publicly display, create derivative works from, or commercially exploit Site content without our prior written permission, except as permitted by law.

“Build.Make.Win,” the Build.Make.Win peak mark, “CoachER,” associated logos, and related names and branding are trademarks or proprietary brand assets of Build Make Win Ventures LLC or its affiliates. No license to use those marks is granted by these Terms.

## 6. Messages, Feedback, and Submissions

If you contact us, you represent that your message is lawful and that you have the right to send any information it contains. You retain ownership of material you submit.

You grant us a limited, non-exclusive license to use, reproduce, and share your submission only as reasonably necessary to review it, respond to you, operate our business, protect our rights, or comply with law.

Do not send trade secrets, confidential information, sensitive personal information, or proprietary product ideas through a general contact channel.

Unless we have entered into a separate written confidentiality agreement, general inquiries and unsolicited ideas will not be treated as confidential.

## 7. Third-Party Links and Services

The Site may link to third-party websites, app stores, social platforms, or other services. Links are provided for convenience and do not necessarily imply endorsement.

We do not control and are not responsible for third-party content, products, availability, security, or practices. Your use of a third-party service is governed by that service’s terms and policies.

## 8. Informational Content

Site content is provided for general informational purposes. It is not legal, financial, investment, medical, safety, or other professional advice. You are responsible for evaluating information and deciding whether and how to rely on it.

We try to keep Site content useful and accurate, but content may be incomplete, outdated, or contain errors. Product features, availability, descriptions, release timing, and plans may change without notice.

## 9. Site Availability and Changes

We may change, suspend, or discontinue any part of the Site at any time.

We do not guarantee that the Site will always be available, uninterrupted, secure, or error-free. We may update these Terms as described in Section 15.

## 10. Disclaimer of Warranties

TO THE FULLEST EXTENT PERMITTED BY LAW, THE SITE AND ALL CONTENT ARE PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.

BUILD MAKE WIN DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.

Some jurisdictions do not allow certain warranty disclaimers. In those jurisdictions, the disclaimers apply only to the extent permitted by law.

## 11. Limitation of Liability

TO THE FULLEST EXTENT PERMITTED BY LAW, BUILD MAKE WIN AND ITS MEMBERS, MANAGERS, OFFICERS, EMPLOYEES, CONTRACTORS, AGENTS, LICENSORS, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES; LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITY; OR DAMAGES ARISING FROM OR RELATED TO YOUR ACCESS TO, USE OF, OR INABILITY TO USE THE SITE.

TO THE FULLEST EXTENT PERMITTED BY LAW, THE TOTAL LIABILITY OF BUILD MAKE WIN AND THE OTHER PARTIES LISTED ABOVE FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SITE OR THESE TERMS WILL NOT EXCEED **ONE HUNDRED U.S. DOLLARS (US $100)**.

These limitations do not apply to liability that cannot lawfully be limited or excluded. Some jurisdictions do not allow certain limitations of liability, so some limitations may not apply to you.

## 12. Indemnification

To the extent permitted by law, you agree to defend, indemnify, and hold harmless Build Make Win and its members, managers, officers, employees, contractors, and agents from claims, liabilities, damages, judgments, losses, and reasonable costs arising out of:

- Your unlawful use of the Site;
- Your material violation of these Terms; or
- Your infringement of another person’s rights.

This section does not require a consumer to indemnify Build Make Win for Build Make Win’s own unlawful conduct, negligence, or willful misconduct.

## 13. Governing Law and Venue

These Terms are governed by the laws of the State of Ohio, without regard to conflict-of-law principles.

Subject to any rights that cannot be waived under applicable law, any legal action arising out of or relating to the Site or these Terms must be brought in a state or federal court with jurisdiction where Build Make Win maintains its principal place of business in Ohio. Each party consents to the personal jurisdiction of those courts.

## 14. Dispute Resolution

Before filing a legal claim, you and Build Make Win agree to make a good-faith effort to resolve the dispute informally.

To begin that process, send a written description of the dispute and the relief requested to **support@buildmakewin.com**.

Either party may proceed with a formal claim if the dispute is not resolved within 30 days after the notice is received.

This section does not prevent either party from seeking temporary or emergency injunctive relief, and it does not require arbitration.

## 15. Changes to These Terms

We may update these Terms from time to time. We will post the revised Terms on this page and update the “Last updated” date.

Changes apply prospectively when posted unless a later date is stated. Your continued use of the Site after updated Terms take effect means you accept the updated Terms.

## 16. General Terms

These Terms and the Privacy Policy are the entire agreement between you and Build Make Win regarding the Site, except for any separate written agreement that expressly applies.

If a provision of these Terms is held unenforceable, it will be modified to the minimum extent necessary to make it enforceable, and the remaining provisions will remain in effect.

Our failure to enforce a provision is not a waiver. You may not assign these Terms without our written consent. We may assign them in connection with a reorganization, merger, acquisition, financing, sale of assets, or by operation of law.

Headings are for convenience only. The words “including” and “includes” mean “including without limitation.”

## 17. Contact Us

Questions about these Terms may be sent to:

**Build Make Win Ventures LLC**
Ohio, United States
**Email:** support@buildmakewin.com
`;
